import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "pwa-install-dismissed-at";
const REMIND_HOURS = 72; // ask again after 3 days

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

function isRecentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const then = new Date(raw).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then < REMIND_HOURS * 3600 * 1000;
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Shows a discreet bottom banner offering to install the app.
 * - Android/Chrome: uses beforeinstallprompt event (native prompt on tap).
 * - iOS Safari: shows an inline hint (no programmatic install).
 * User can dismiss; banner won't reappear for REMIND_HOURS.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIOS()) {
      const t = setTimeout(() => setIosHint(true), 8000); // subtle delay
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome !== "accepted") localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setDeferred(null);
    setShow(false);
  };

  if (!show && !iosHint) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-3 lg:right-6 lg:left-auto lg:max-w-sm z-40 rounded-xl bg-white shadow-lg border border-slate-200 p-4 flex items-start gap-3 fade-in"
      role="dialog" aria-label="Installer l'application"
    >
      <div className="h-10 w-10 rounded-lg bg-[#0066FF]/10 flex items-center justify-center shrink-0">
        <Download className="h-5 w-5 text-[#0066FF]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 text-sm">Installer FIO RH</div>
        {show ? (
          <p className="text-xs text-slate-600 mt-0.5">
            Accédez à votre espace en un tap, hors ligne compatible, sans passer par le navigateur.
          </p>
        ) : (
          <p className="text-xs text-slate-600 mt-0.5">
            Appuyez sur <b>Partager</b> puis <b>« Sur l'écran d'accueil »</b> pour installer l'application.
          </p>
        )}
        {show && (
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={install} data-testid="pwa-install-accept" className="bg-[#0066FF] hover:bg-[#0052CC]">
              Installer
            </Button>
            <Button size="sm" variant="outline" onClick={dismiss} data-testid="pwa-install-later">
              Plus tard
            </Button>
          </div>
        )}
      </div>
      <button
        onClick={dismiss} aria-label="Fermer"
        data-testid="pwa-install-close"
        className="shrink-0 h-8 w-8 -mr-1 -mt-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
